import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Store,
  Users,
  QrCode,
  Layers,
  Inbox,
  Clock,
  ArrowRight,
  TrendingUp,
  AlertCircle,
  HelpCircle,
} from "lucide-react";
import { apiBoss } from "@/lib/api";

export default function BossDashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiBoss.get("/boss/dashboard")
      .then(({ data }) => setData(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading || !data) {
    return (
      <div className="py-20 text-center text-slate-400 font-mono text-sm">
        Loading Boss KPI Metrics...
      </div>
    );
  }

  const kpis = [
    { label: "Total Cafes", value: data.total_cafes, icon: Store, color: "text-amber-400", bg: "bg-amber-500/10" },
    { label: "Total Customers Connected", value: data.total_customers.toLocaleString(), icon: Users, color: "text-blue-400", bg: "bg-blue-500/10" },
    { label: "Total QR Menus", value: data.total_qr_menus, icon: QrCode, color: "text-emerald-400", bg: "bg-emerald-500/10" },
    { label: "Total Tables", value: data.total_tables.toLocaleString(), icon: Layers, color: "text-purple-400", bg: "bg-purple-500/10" },
    { label: "New Leads", value: data.new_leads, icon: Inbox, color: "text-primary", bg: "bg-primary/10", alert: data.new_leads > 0 },
    { label: "Pending Follow-ups", value: data.pending_followups, icon: Clock, color: "text-orange-400", bg: "bg-orange-500/10" },
  ];

  return (
    <div className="space-y-10 max-w-7xl mx-auto">
      {/* Header Banner */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-slate-800 pb-6">
        <div>
          <span className="text-xs font-mono uppercase tracking-[0.2em] text-primary font-semibold">Overview</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white mt-1">Boss Control Dashboard</h1>
        </div>

        {data.new_leads > 0 && (
          <div className="bg-primary/15 border border-primary/30 text-primary px-4 py-2 rounded-2xl text-xs font-semibold flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            <span>{data.new_leads} new cafe inquiry lead(s) waiting!</span>
          </div>
        )}
      </div>

      {/* KPI Cards Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {kpis.map((kpi) => {
          const Icon = kpi.icon;
          return (
            <div key={kpi.label} className="bg-slate-900 border border-slate-800 rounded-3xl p-5 space-y-3 relative overflow-hidden">
              {kpi.alert && (
                <span className="absolute top-3 right-3 w-2.5 h-2.5 rounded-full bg-primary animate-ping" />
              )}
              <div className={`w-10 h-10 rounded-2xl ${kpi.bg} ${kpi.color} grid place-items-center`}>
                <Icon className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-wider text-slate-400 leading-tight">
                  {kpi.label}
                </div>
                <div className="font-heading text-2xl sm:text-3xl font-bold text-white mt-1">
                  {kpi.value}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent Leads & Recent Cafes Section */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Recent Leads Table */}
        <div className="lg:col-span-7 bg-slate-900 border border-slate-800 rounded-3xl p-6 space-y-4">
          <div className="flex items-center justify-between border-b border-slate-800 pb-4">
            <div className="flex items-center gap-2">
              <Inbox className="w-5 h-5 text-primary" />
              <h2 className="font-heading text-xl font-bold text-white">Recent Cafe Leads</h2>
            </div>
            <Link to="/boss/leads" className="text-xs text-primary font-semibold hover:underline flex items-center gap-1">
              View All Pipeline <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          <div className="divide-y divide-slate-800/80">
            {data.recent_leads.length === 0 ? (
              <div className="py-8 text-center text-xs text-slate-500 font-mono">No contact form leads received yet.</div>
            ) : (
              data.recent_leads.map((lead) => (
                <div key={lead.id} className="py-3 flex items-center justify-between text-xs gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-white truncate">{lead.cafe_name}</div>
                    <div className="text-slate-400 text-[11px] truncate">Owner: {lead.name} • {lead.mobile}</div>
                  </div>

                  <div className="text-right shrink-0 space-y-1">
                    <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-mono font-semibold ${
                      lead.status === "New" ? "bg-primary/20 text-primary border border-primary/30" : "bg-slate-800 text-slate-300"
                    }`}>
                      {lead.status}
                    </span>
                    <div className="text-[10px] text-slate-500 font-mono">{lead.location}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Recent Active Cafes */}
        <div className="lg:col-span-5 bg-slate-900 border border-slate-800 rounded-3xl p-6 space-y-4">
          <div className="flex items-center justify-between border-b border-slate-800 pb-4">
            <div className="flex items-center gap-2">
              <Store className="w-5 h-5 text-amber-400" />
              <h2 className="font-heading text-xl font-bold text-white">Active Cafes Showcase</h2>
            </div>
            <Link to="/boss/cafes" className="text-xs text-amber-400 font-semibold hover:underline flex items-center gap-1">
              Manage Cafes <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          <div className="space-y-3">
            {data.recent_cafes.map((cafe) => (
              <div key={cafe.id} className="p-3 bg-slate-950 rounded-2xl border border-slate-800 flex items-center justify-between text-xs">
                <div className="flex items-center gap-3">
                  <img
                    src={cafe.logo_url || "https://images.unsplash.com/photo-1554118811-1e0d58224f24?w=100&auto=format&fit=crop"}
                    alt="Logo"
                    className="w-9 h-9 rounded-xl object-cover"
                  />
                  <div>
                    <div className="font-bold text-white">{cafe.cafe_name}</div>
                    <div className="text-[10px] text-slate-400">{cafe.location} • {cafe.table_count} Tables</div>
                  </div>
                </div>

                <Link
                  to={`/menu/${cafe.slug}`}
                  target="_blank"
                  className="px-3 py-1 rounded-xl bg-slate-800 hover:bg-primary text-white text-[11px] font-semibold transition-colors"
                >
                  Menu
                </Link>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
